import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { AdvisorPicker } from "@/components/ai-assistant/advisor";
import { FileDropZone } from "@/components/ai-assistant/file-drop-zone";
import {
  MAYA_FORM_MAX_MSG,
  MayaForm,
} from "@/components/ai-assistant/maya-form";
import { useMayaFileUploads } from "@/hooks/useMayaFileUploads";

import { MAYA_SIDEBAR_ID, PAGE_LENS_ENABLED } from "../constants";
import { useChat } from "../context/use-chat";
import { useMayaSidebar } from "../context/use-maya-sidebar";
import { useMayaSpace } from "../context/use-maya-space";
import { sessionGet, sessionRemove, sessionSet } from "../utils/safe-storage";

import { ChatHeader } from "./chat-header";
import { ChatsView } from "./chats-view";
import {
  Composer,
  type ComposerHandle,
  type ComposerUploads,
} from "./composer";
import { ContextLens } from "./context-lens";
import { MessageList } from "./message-list";

interface ChatPanelProps {
  onClose: () => void;
}

export const ChatPanel = ({ onClose }: ChatPanelProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const {
    sendMessage,
    isTyping,
    stop,
    loadingConversation,
    pendingReplyId,
    resetChat,
    activeForm,
    closeForm,
    temporary,
    advisor,
    selectAdvisor,
  } = useChat();

  const space = useMayaSpace();
  // Files for the turn being written: picked, pasted, or dropped anywhere on
  // the conversation. Uploaded straight away; the turn carries their links.
  const {
    attachments,
    addFiles,
    removeAttachment,
    retryAttachment,
    clear: clearAttachments,
    hasUploading,
    successAttachments,
  } = useMayaFileUploads(space.endpoints.upload);
  const uploads = useMemo<ComposerUploads>(
    () => ({
      attachments,
      addFiles,
      removeAttachment,
      retryAttachment,
      hasUploading,
      readyCount: successAttachments.length,
    }),
    [
      attachments,
      addFiles,
      removeAttachment,
      retryAttachment,
      hasUploading,
      successAttachments.length,
    ],
  );
  const [draft, setDraft] = useState<string>(
    () => sessionGet(space.storage.draft) ?? "",
  );
  const composerRef = useRef<ComposerHandle | null>(null);
  const { focusComposerRef } = useMayaSidebar();

  // Into the composer when the user opened Maya — not when the sidebar is
  // restored on reload, which would steal focus from the page. The flag is
  // consumed inside the timeout so StrictMode's mount/unmount/mount still
  // focuses once.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!focusComposerRef.current) return;
      focusComposerRef.current = false;
      composerRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(t);
  }, [focusComposerRef]);

  useEffect(() => {
    if (draft) sessionSet(space.storage.draft, draft);
    else sessionRemove(space.storage.draft);
  }, [draft, space]);

  // The chat switcher replaces the conversation in place; the draft (state
  // here) survives the round trip.
  const [view, setView] = useState<"conversation" | "chats">("conversation");
  const chatsButtonRef = useRef<HTMLButtonElement | null>(null);
  // Where focus goes once the view has switched and its elements exist.
  const focusAfterSwitchRef = useRef<"composer" | "chats-button" | null>(null);
  useEffect(() => {
    const target = focusAfterSwitchRef.current;
    focusAfterSwitchRef.current = null;
    if (target === "composer") composerRef.current?.focus();
    else if (target === "chats-button") chatsButtonRef.current?.focus();
  }, [view]);

  const closeChats = useCallback(() => {
    focusAfterSwitchRef.current = "chats-button";
    setView("conversation");
  }, []);
  const openedFromChats = useCallback(() => {
    focusAfterSwitchRef.current = "composer";
    setView("conversation");
  }, []);
  // New chat from the header: always lands in the (empty) conversation with
  // the cursor in the composer — the button itself disappears once the chat
  // is empty, so focus must not stay with it.
  const newChat = useCallback(() => {
    resetChat();
    focusAfterSwitchRef.current = "composer";
    if (view === "conversation") composerRef.current?.focus();
    else setView("conversation");
  }, [resetChat, view]);

  const toggleChats = useCallback(() => {
    if (view === "chats") closeChats();
    else setView("chats");
  }, [view, closeChats]);

  const handleSubmit = (text: string) => {
    // Keep the draft (and files) while a saved chat is still opening, or an
    // edited prompt's reply is still on its way.
    if (
      (!text && successAttachments.length === 0) ||
      hasUploading ||
      loadingConversation ||
      pendingReplyId
    )
      return;
    void sendMessage(text, successAttachments);
    setDraft("");
    clearAttachments();
  };

  // Closing the form (X, Cancel, Escape) hands focus back to the composer
  // instead of dropping it on the page.
  const dismissForm = useCallback(() => {
    closeForm();
    composerRef.current?.focus();
  }, [closeForm]);
  // A form arriving takes focus only when the user is in the sidebar but not
  // in the composer — while typing there (allowed during a reply), moving
  // focus would send the next keystrokes into the form.
  const shouldFocusForm = useCallback(() => {
    const active = document.activeElement;
    return (
      !!active?.closest(`#${MAYA_SIDEBAR_ID}`) &&
      !active.closest(".maya-composer")
    );
  }, []);

  // A form Maya asked for: its answers become "Label: value" lines added to
  // the draft for the user to review, attach to and send — never auto-sent.
  const handleFormSubmit = (text: string, trimmed: boolean) => {
    let merged = draft.trim() ? `${draft.trim()}\n${text}` : text;
    let didTrim = trimmed;
    if (merged.length > MAYA_FORM_MAX_MSG) {
      merged = merged.slice(0, MAYA_FORM_MAX_MSG);
      didTrim = true;
    }
    composerRef.current?.setValue(merged);
    closeForm();
    toast.success(
      t("my-space:toasts.added_to_your_message_edit_attach_then_send"),
    );
    if (didTrim) toast(t("my-space:toasts.trimmed_to_fit_the_message_limit"));
  };

  return (
    // Named by the surrounding <aside>.
    <div className="flex flex-col h-full">
      {/* Mounted for the panel's lifetime so the change is announced (a live
          region that appears with its text already in it isn't read). */}
      <span role="status" className="sr-only">
        {loadingConversation ? t("common:maya.loading_chat") : ""}
      </span>
      <ChatHeader
        onClose={onClose}
        chatsOpen={view === "chats"}
        onToggleChats={toggleChats}
        chatsButtonRef={chatsButtonRef}
        onNewChat={newChat}
      />
      {view === "chats" ? (
        <ChatsView onClose={closeChats} onOpened={openedFromChats} />
      ) : (
        // Files dropped anywhere on the conversation attach to the draft.
        <FileDropZone
          onFiles={addFiles}
          disabled={loadingConversation}
          className="flex min-h-0 flex-1 flex-col"
        >
          {PAGE_LENS_ENABLED && <ContextLens />}
          <MessageList />
          <div>
            {/* The form sits above the composer — a sibling, never inside
                its <form>, which would be invalid HTML. In the flow rather
                than floating, so it never covers the end of the reply that
                asked for it (the log shrinks and stays on its last line). */}
            <AnimatePresence>
              {activeForm && (
                <motion.div
                  key="maya-form"
                  initial={{ opacity: 0, y: 16, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 12, scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 460, damping: 32 }}
                  className="mx-3 mb-1 max-h-[45vh] origin-bottom overflow-y-auto"
                >
                  <MayaForm
                    spec={activeForm}
                    onSubmit={handleFormSubmit}
                    onClose={dismissForm}
                    autoFocus={shouldFocusForm}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <Composer
              ref={composerRef}
              value={draft}
              onChange={setDraft}
              onSubmit={handleSubmit}
              uploads={uploads}
              temporary={temporary}
              placeholder={t(advisor.placeholder)}
              actionSlot={
                <AdvisorPicker
                  advisor={advisor}
                  onSelect={selectAdvisor}
                  disabled={isTyping}
                  isPersonal={space.personal}
                />
              }
              isStreaming={isTyping}
              onStop={stop}
              sendBlocked={loadingConversation || pendingReplyId !== null}
            />
          </div>
          <p className="pb-2 text-center text-[9px] text-muted-foreground">
            {t("common:maya.powered_by_disclaimer")}
          </p>
        </FileDropZone>
      )}
    </div>
  );
};
